import Button from "@/common/elements/Button";
import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import {
  StyledGridCol,
  StyledGridRow,
  StyledInformationList,
  StyledSectionHeading,
} from "@/common/styles";
import JailIncidentInvolvedInmateInformation from "@/components/BookingsDetails/JailIncidentsInvolvedDetail/JailIncidentInvolvedInmateInformation";
import {
  getOptionsFromRefList,
  getRefListNameByID,
  isNonEmptyVariable,
} from "@/util/common_function";
import { Form, TimePicker } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

const DATE_FORMATS = ["MM/DD/YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"];
const TIME_FORMATS = ["HH:mm", "HH:mm:ss"];

const parseDateValue = (value) => {
  if (!isNonEmptyVariable(value)) {
    return undefined;
  }
  const parsed = dayjs(value, DATE_FORMATS, true);
  return parsed.isValid() ? parsed : undefined;
};

const parseTimeValue = (value) => {
  if (!isNonEmptyVariable(value)) {
    return undefined;
  }
  const parsed = dayjs(value, TIME_FORMATS, true);
  return parsed.isValid() ? parsed : undefined;
};

export default function InmateWatchDetail({
  bookings,
  commentlog,
  modalType,
  form,
}) {
  const [category, setCategory] = useState(null);

  useEffect(() => {
    if (!isNonEmptyVariable(commentlog)) {
      form.resetFields();
      setCategory(null);
      return;
    }

    const formValues = {};

    if (isNonEmptyVariable(commentlog.commentLogScopeID)) {
      const scope = Number(commentlog.commentLogScopeID);
      formValues.commentLogScopeID = Number.isNaN(scope)
        ? commentlog.commentLogScopeID
        : scope;
    }

    if (isNonEmptyVariable(commentlog.shiftID)) {
      const shift = Number(commentlog.shiftID);
      formValues.shiftID = Number.isNaN(shift) ? commentlog.shiftID : shift;
    }

    if (
      Array.isArray(commentlog.inmateWatch) &&
      commentlog.inmateWatch.length > 0
    ) {
      formValues.inmateWatch = commentlog.inmateWatch.map((item) => ({
        ...item,
      }));
    }

    const parsedDate = parseDateValue(commentlog.expirationDate);
    if (parsedDate) {
      formValues.expirationDate = parsedDate;
    }

    const parsedTime = parseTimeValue(commentlog.expirationTime);
    if (parsedTime) {
      formValues.expirationTime = parsedTime;
    }

    if (isNonEmptyVariable(commentlog.comments)) {
      formValues.comments = commentlog.comments;
    }

    form.setFieldsValue(formValues);

    if (isNonEmptyVariable(commentlog.commentLogCategoryID)) {
      const categoryId = Number(commentlog.commentLogCategoryID);
      const lookupId = Number.isNaN(categoryId)
        ? commentlog.commentLogCategoryID
        : categoryId;
      const name = getRefListNameByID(
        "Comment_Log_Category",
        lookupId,
        false,
        false
      );
      setCategory(isNonEmptyVariable(name) ? name : null);
    } else {
      setCategory(null);
    }
  }, [commentlog, form]);

  const handleExpireShift = () => {
    form.setFieldsValue({
      expirationDate: dayjs(),
      expirationTime: dayjs(),
    });
  };

  return (
    <Form form={form}>
      <StyledInformationList style={{ padding: "0" }}>
        <StyledSectionHeading>
          <span>Watch Details</span>
        </StyledSectionHeading>

        <div className="result--full-width" style={{ paddingTop: "8px" }}>
          <StyledGridRow gutter={24}>
            <StyledGridCol lg={6}>
              <FormItem label="Shift" name="shiftID">
                <Select
                  options={getOptionsFromRefList("Shift", false, false, true)}
                  disabled={modalType === "view"}
                />
              </FormItem>
            </StyledGridCol>

            <StyledGridCol lg={18}>
              <StyledGridRow gutter={24}>
                <StyledGridCol>
                  <FormItem
                    label="Interval"
                    className="key"
                    name={["inmateWatch", 0, "interval"]}
                  >
                    <TextField disabled={modalType === "view"} />
                  </FormItem>
                </StyledGridCol>

                <StyledGridCol lg={8}>
                  <div className="key">watchGroup</div>
                  <p className="value">
                    {commentlog?.watchGroup ? commentlog.watchGroup : "-"}
                  </p>
                </StyledGridCol>
              </StyledGridRow>
            </StyledGridCol>
            <StyledGridCol lg={18}>
              <StyledGridRow gutter={24}>
                {modalType !== "view" && (
                  <StyledGridCol lg={6} marginTop="30px">
                    <FormItem>
                      <Button onClick={handleExpireShift}>Expire Shift</Button>
                    </FormItem>
                  </StyledGridCol>
                )}

                <StyledGridCol>
                  <FormItem
                    label="Expire Date"
                    className="key"
                    name="expirationDate"
                  >
                    <CommonDatePicker
                      disabled={modalType === "view"}
                      showTime={false}
                      format="MM/DD/YYYY"
                    />
                  </FormItem>
                </StyledGridCol>

                <StyledGridCol>
                  <FormItem
                    label="Expire Time"
                    className="key"
                    name="expirationTime"
                  >
                    <TimePicker
                      disabled={modalType === "view"}
                      showTime={false}
                    />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>
            </StyledGridCol>
            <StyledGridCol lg={24} span={24}>
              <FormItem label="Comments" name="comments">
                <TextField
                  inputType="textarea"
                  rows="4"
                  disabled={modalType === "view"}
                />
              </FormItem>
            </StyledGridCol>

            <StyledGridCol lg={24} span={24}>
              <StyledGridRow gutter={16}>
                <StyledGridCol>
                  <div className="key">Due Date/Time</div>
                  <p className="value">
                    {commentlog?.dueDate
                      ? `${commentlog.dueDate} ${commentlog.dueTime || ""}`
                      : "-"}
                  </p>
                </StyledGridCol>

                <StyledGridCol>
                  <div className="key">actualDateTime</div>
                  <p className="value">
                    {commentlog?.actualDate
                      ? `${commentlog.actualDate} ${
                          commentlog.actualTime || ""
                        }`
                      : "-"}
                  </p>
                </StyledGridCol>

                <StyledGridCol>
                  <div className="key">Category</div>
                  <p className="value">{category ? category : "-"}</p>
                </StyledGridCol>
              </StyledGridRow>
            </StyledGridCol>
          </StyledGridRow>
        </div>

        {(!isNonEmptyVariable(bookings) && (
          <>
            <StyledSectionHeading>
              <span>Location</span>
            </StyledSectionHeading>

            <div className="result--full-width" style={{ paddingTop: "8px" }}>
              <StyledGridRow gutter={24}>
                <StyledGridCol>
                  <div className="key">Agency</div>
                  <p className="value">{commentlog?.housingAgency || "-"}</p>
                </StyledGridCol>

                <StyledGridCol>
                  <div className="key">Block</div>
                  <p className="value">
                    {commentlog?.block ? commentlog.block : "-"}
                  </p>
                </StyledGridCol>

                <StyledGridCol>
                  <div className="key">Cell</div>
                  <p className="value">
                    {commentlog?.cell ? commentlog.cell : "-"}
                  </p>
                </StyledGridCol>

                <StyledGridCol>
                  <div className="key">Bed</div>
                  <p className="value">
                    {commentlog?.bed ? commentlog.bed : "-"}
                  </p>
                </StyledGridCol>
              </StyledGridRow>
            </div>
          </>
        )) || (
          <JailIncidentInvolvedInmateInformation
            showBasicInfo={true}
            showTopBar={true}
            from="facilityLog"
            selectedInmate={bookings}
            disabled={true}
          />
        )}
      </StyledInformationList>
    </Form>
  );
}
